import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { graphqlClient } from "@/lib/graphql";
import { gql } from "graphql-request";
import type { Game } from "@/types";
import { GamePlaceholder } from "@/components/ui/GamePlaceholder";

const SEARCH_GAMES_QUERY = gql`
  query SearchGames($query: String!) {
    searchGames(query: $query) {
      id
      title
      imageUrl
      thumbnailUrl
      yearPublished
    }
  }
`;

interface SearchBarProps {
  initialValue?: string;
  onSearch?: (query: string) => void;
  onChange?: (query: string) => void;
  autoFocus?: boolean;
  size?: "default" | "lg";
  placeholder?: string;
}

export function SearchBar({
  initialValue = "",
  onSearch,
  onChange,
  autoFocus = false,
  size = "default",
  placeholder,
}: SearchBarProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const effectivePlaceholder =
    placeholder ?? t("games_page.search_example_placeholder");
  const [query, setQuery] = useState(initialValue);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setQuery(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isFetching } = useQuery({
    queryKey: ["searchGamesAutocomplete", debouncedQuery],
    queryFn: () =>
      graphqlClient.request<{ searchGames: Game[] }>(SEARCH_GAMES_QUERY, {
        query: debouncedQuery,
      }),
    enabled: debouncedQuery.length >= 2,
  });

  const results = data?.searchGames || [];

  const runSearch = useCallback(
    (searchText: string) => {
      const trimmed = searchText.trim();
      if (!trimmed) return;
      setIsOpen(false);
      inputRef.current?.blur();
      if (onSearch) {
        onSearch(trimmed);
      } else {
        // Price-tracker–first: global search hits the games catalog. From there
        // users can pivot to community listings if they're shopping used.
        navigate(`/games?q=${encodeURIComponent(trimmed)}`);
        setQuery("");
      }
    },
    [navigate, onSearch]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) {
      if (e.key === "Enter" && query.trim()) {
        e.preventDefault();
        runSearch(query);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((prev) =>
          prev > 0 ? prev - 1 : results.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < results.length) {
          runSearch(results[activeIndex].title);
        } else if (query.trim()) {
          runSearch(query);
        }
        break;
      case "Escape":
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setActiveIndex(-1);
  }, [results]);

  const inputClass =
    size === "lg" ? "input py-4 pl-12 text-lg" : "input pl-10";
  const iconClass =
    size === "lg"
      ? "absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
      : "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400";
  const spinnerClass =
    size === "lg"
      ? "absolute right-4 top-1/2 -translate-y-1/2"
      : "absolute right-3 top-1/2 -translate-y-1/2";

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <svg
          className={iconClass}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={query}
          autoFocus={autoFocus}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            onChange?.(e.target.value);
          }}
          onFocus={() => {
            if (query.trim().length >= 2) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={effectivePlaceholder}
          className={inputClass}
          role="combobox"
          aria-expanded={isOpen && results.length > 0}
          aria-autocomplete="list"
          aria-controls="search-results"
        />
        {isFetching && (
          <div className={spinnerClass}>
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary-800" />
          </div>
        )}
      </div>

      {isOpen && debouncedQuery.length >= 2 && results.length > 0 && (
        <div
          id="search-results"
          role="listbox"
          className="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
        >
          <ul className="max-h-80 overflow-y-auto py-1">
            {results.map((game, index) => (
              <li
                key={game.id}
                role="option"
                aria-selected={index === activeIndex}
                className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 transition-colors ${
                  index === activeIndex
                    ? "bg-primary-50 text-primary-800"
                    : "text-gray-900 hover:bg-gray-50"
                }`}
                onClick={() => {
                  setIsOpen(false);
                  setQuery("");
                  navigate(`/games/${game.id}`);
                }}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                  {game.imageUrl || game.thumbnailUrl ? (
                    <img
                      src={game.imageUrl || game.thumbnailUrl || ""}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <GamePlaceholder />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{game.title}</p>
                  {game.yearPublished && (
                    <p className="text-xs text-gray-500">
                      {game.yearPublished}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => runSearch(query)}
            className="block w-full border-t border-gray-100 bg-gray-50 px-4 py-2.5 text-left text-xs font-medium text-primary-800 hover:bg-gray-100"
          >
            Ieškoti pagal &ldquo;{query.trim()}&rdquo;
          </button>
        </div>
      )}
    </div>
  );
}
